import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { InputWrapper, LabelStyle } from "../../Common/styles/form";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { ModalActionButtonStyles } from "../../Common/styles/modal";
import { DrawerForm } from "../../Common/styles/drawer";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";

type Props = {
  data?: any;
  // handleNext: () => void;
  // setStudyId: (value: string) => void;
};
const studyTypeOptions = [
  { label: "Observational Study", value: "observational_study" },
  { label: "Interventional Study", value: "interventional_study" },
  { label: "Patient Registry", value: "patient_registry" },
];

let schema = yup.object().shape({
  name: yup
    .string()
    .required("Study Name is Required")
    .trim()
    .min(4, "Study Name must be at least 4 Characters Long")
    .max(100, "Study Name cannot be greater than 100 Characters"),
  type: yup.string().required("Study Type is Required"),
  // principalInvastigator: yup.string().optional(),

  siteName: yup
    .string()
    .trim()
    .required("Site Name is Required")
    .max(100, "Site Name cannot be greater than 100 Characters"),
  siteAbbreviation: yup.string().required("Site Abbreviation is Required"),
  countryId: yup.string().required("Country is Required"),
  // siteCode: yup.string().required("Site Code is Required"),
  // address: yup.string().optional(),
});
// { data, handleNext, setStudyId }: Props
const GeneralAndSiteInfo = ({ data }: Props) => {
  const navigation = useNavigate();
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchLoader, setSearchLoader] = useState<boolean>(false);
  const [countriesList, setCountriesList] = useState<any[]>([]);
  const [sitesList, setSitesList] = useState<any[]>([]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        const res: AxiosResponse = await http.get(`/data/countries`);
        const newData = res?.data?.data?.map((country: any) => ({
          value: country?.valueId,
          label: country?.label,
        }));
        setCountriesList(newData);
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchCountries();
  }, []);

  const handleSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        try {
          setSearchLoader(true);
          setSitesList([]);
          let url = `/registry/sites?query=${value}`;

          const res: AxiosResponse = await http.get(url);
          const sites = res.data?.data;
          const newData = sites?.map((site: any) => ({
            id: site?.id,
            siteName: site?.name,
            siteAbbreviation: site?.abbreviation,
            countryId: site?.countryId,
            country: site?.country?.label,
          }));

          setSitesList(newData);
          setSearchLoader(false);
        } catch (err) {
          errorToastMessage(err as Error);
          setSearchLoader(false);
        }
      }, 500),
    []
  );

  useEffect(() => {
    handleSearch("");
  }, [handleSearch]);

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      const body = {
        name: values?.name,
        type: values?.type,
        displayLogicStrategy: values?.displayLogicStrategy,
        // principalInvastigator: values?.principalInvastigator,
        site: {
          name: values?.siteName,
          abbreviation: values?.siteAbbreviation,
          countryId: values?.countryId,
          // code: values?.siteCode,
          // address: values?.address,
        },
      };
      let res: AxiosResponse;
      if (data?.id) {
        res = await http.patch(`/study/${data?.id}`, body);
      } else {
        res = await http.post(`/study`, body);
      }
      const study = res?.data?.data?.id;
      // setStudyId(study);
      navigation(`/studies/${study}/study-designer`);
      toastMessage("success", res?.data?.message);
      setSubmitLoader(false);
      // handleNext();
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };
  return (
    <>
      {/* <Typography fontSize="16px" fontWeight={400} color="text.secondary">
        Provide key details about the study, including its name, description,
        and site information to establish a solid foundation.
      </Typography>
      <Divider sx={{ my: 3 }} /> */}
      {!loading ? (
        <Formik
          initialValues={{
            name: data?.studyName || "",
            type: data?.type || "observational_study",
            // principalInvastigator: data?.principalInvestigator || "",
            siteName: data?.siteName || "",
            siteAbbreviation: data?.abbreviation || "",
            countryId: data?.countryId || "",
            displayLogicStrategy: data?.displayLogicStrategy || "dependencies",
            // siteCode: data?.siteCode || "",
            // address: data?.address || "",
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ getFieldProps, errors, touched, setFieldValue, values }) => (
            <Form style={DrawerForm}>
              <Typography
                fontWeight={500}
                fontSize={18}
                color="text.primary"
                mb={2.5}
              >
                General Information
              </Typography>
              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="study-name">
                  Study Name <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <TextField
                  placeholder="Study name"
                  id="study-name"
                  {...getFieldProps("name")}
                  inputProps={{
                    maxLength: 100,
                  }}
                  error={touched?.name && errors?.name ? true : false}
                  helperText={
                    touched?.name && errors?.name
                      ? (errors?.name as string)
                      : " "
                  }
                />
              </FormControl>
              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="study-type">
                  Study Type <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <Select
                  value={values.type}
                  onChange={(e) => {
                    setFieldValue("type", e.target.value);
                  }}
                  fullWidth
                  id="study-type"
                >
                  {studyTypeOptions.map((option: any) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText
                  error={touched?.type && errors?.type ? true : false}
                >
                  {touched?.type && errors?.type
                    ? (errors?.type as string)
                    : " "}
                </FormHelperText>
              </FormControl>

              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="displayLogicStrategy">
                  Skip Logic
                </FormLabel>
                <Select
                  id="displayLogicStrategy"
                  value={values.displayLogicStrategy}
                  onChange={(e) =>
                    setFieldValue("displayLogicStrategy", e.target.value)
                  }
                >
                  {[
                    {
                      key: "dependencies",
                      label: "Dependencies",
                    },
                    {
                      key: "logic_jumps",
                      label: "Logic Jumps",
                    },
                  ].map((item: any) => (
                    <MenuItem key={item.key} value={item.key}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="principal-invastigator">
                  Principal Invastigator
                </FormLabel>
                <TextField
                  placeholder="Principal Invastigator"
                  id="principal-invastigator"
                  {...getFieldProps("principalInvastigator")}
                  error={
                    touched?.principalInvastigator &&
                    errors?.principalInvastigator
                      ? true
                      : false
                  }
                  helperText={
                    touched?.principalInvastigator &&
                    errors?.principalInvastigator
                      ? (errors?.principalInvastigator as string)
                      : " "
                  }
                />
              </FormControl> */}
              <Divider sx={{ mt: 2, mb: 3 }} />

              <Typography
                fontWeight={500}
                fontSize={18}
                color="text.primary"
                mb={2.5}
              >
                Site Information
              </Typography>

              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle}>
                  Site Name <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <Autocomplete
                  freeSolo
                  filterOptions={(x) => x}
                  onInputChange={(_1: any, value: any, reason: string) => {
                    if (reason === "input") {
                      handleSearch(value);
                      setFieldValue("siteName", value);
                    }
                  }}
                  onChange={(_1: any, value: any) => {
                    if (typeof value === "string") {
                      setFieldValue("siteName", value);
                      setFieldValue("siteAbbreviation", "");
                      setFieldValue("countryId", "");
                    } else if (value && value.siteName) {
                      setFieldValue("siteName", value.siteName);
                      setFieldValue("siteAbbreviation", value.siteAbbreviation);
                      setFieldValue("countryId", value.countryId);
                    } else if (value === null) {
                      setFieldValue("siteName", "");
                      setFieldValue("siteAbbreviation", "");
                      setFieldValue("countryId", "");
                    }
                  }}
                  isOptionEqualToValue={(option, value) => {
                    const optionTitle =
                      typeof option === "string" ? option : option.siteName;
                    const valueTitle =
                      typeof value === "string" ? value : value.siteName;
                    return optionTitle === valueTitle;
                  }}
                  value={values?.siteName || ""}
                  options={sitesList}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.siteName
                  }
                  renderOption={(props: any, option: any) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key} {...optionProps}>
                        {option?.siteName}
                      </li>
                    );
                  }}
                  loading={searchLoader}
                  loadingText={<CircularProgress size={20} />}
                  noOptionsText="No Results"
                  clearOnBlur={false}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search Sites by typing..."
                    />
                  )}
                />
                <FormHelperText
                  error={touched?.siteName && errors?.siteName ? true : false}
                >
                  {touched?.siteName && errors?.siteName
                    ? (errors?.siteName as string)
                    : " "}
                </FormHelperText>
              </FormControl>

              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="country-id">
                  Country <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <Select
                  value={values.countryId}
                  onChange={(e) => {
                    setFieldValue("countryId", e.target.value);
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: "300px", overflowY: "auto" },
                    },
                  }}
                  fullWidth
                  id="country-id"
                >
                  {countriesList.map((option: any) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText
                  error={touched?.countryId && errors?.countryId ? true : false}
                >
                  {touched?.countryId && errors?.countryId
                    ? (errors?.countryId as string)
                    : " "}
                </FormHelperText>
              </FormControl>
              {/* <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="site-code">
                  Site Code
                </FormLabel>
                <TextField
                  placeholder="Site code"
                  id="site-code"
                  {...getFieldProps("siteCode")}
                  error={touched?.siteCode && errors?.siteCode ? true : false}
                  helperText={
                    touched?.siteCode && errors?.siteCode
                      ? (errors?.siteCode as string)
                      : " "
                  }
                  inputProps={{ maxLength: 6 }}
                />
              </FormControl> */}
              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="site-abbreviation">
                  Site Abbreviation <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <TextField
                  placeholder="Site abbreviation"
                  id="site-abbreviation"
                  {...getFieldProps("siteAbbreviation")}
                  error={
                    touched?.siteAbbreviation && errors?.siteAbbreviation
                      ? true
                      : false
                  }
                  helperText={
                    touched?.siteAbbreviation && errors?.siteAbbreviation
                      ? (errors?.siteAbbreviation as string)
                      : " "
                  }
                  inputProps={{ maxLength: 6 }}
                />
              </FormControl>
              <Box sx={{ ...ModalActionButtonStyles, mt: "auto" }}>
                {!submitLoader ? (
                  <Button
                    type="submit"
                    variant="contained"
                    onClick={() => console.log(errors)}
                  >
                    Create
                  </Button>
                ) : (
                  <Box sx={{ p: 1 }}>
                    <CircularProgress size={25} />
                  </Box>
                )}
              </Box>
              {/* <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="address">
                  Address
                </FormLabel>
                <TextField
                  placeholder="Address"
                  id="address"
                  {...getFieldProps("address")}
                  error={touched?.address && errors?.address ? true : false}
                  helperText={
                    touched?.address && errors?.address
                      ? (errors?.address as string)
                      : " "
                  }
                />
              </FormControl> */}
            </Form>
          )}
        </Formik>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          <CircularProgress size={25} />
        </Box>
      )}
    </>
  );
};

export default GeneralAndSiteInfo;
